import { Box, Flex, Link, Text } from "@chakra-ui/react";
import { Button } from "components/button/Button";
import { InputGroup } from "components/input/InputGroup";
import { PasswordInputGroup } from "components/input/PasswordInputGroup";
import type { FieldProps } from "formik";
import { Field, Form, Formik } from "formik";
import { capitalize } from "es-toolkit/compat";
import { observer } from "mobx-react";
import { MfaChallenge } from "pages/commons/mfa/MfaChallenge";
import { useEffect } from "react";
import { useStores } from "stores";
import { useLogin } from "api/hooks/useLogin";
import { Userpilot } from "userpilot";
import * as Yup from "yup";
import { ForgotPassword, ForgotPasswordLink } from "./ForgotPassword";
import { GoogleSSOButton } from "./GoogleSSOButton";
import { AUTH_CARD_WIDTH, LandingCopyComponent } from "./registerComponents";
import { ResendEmail } from "./ResendEmail";
import { AuthFormContainer } from "./AuthFormContainer";
import { RouterLink } from "components/routerLink/RouterLink";
import { getRouteApi, Navigate, useRouterState } from "@tanstack/react-router";
import { Loader } from "components/progress/Loader";

const routeApi = getRouteApi("/publicLayout/login");

const loginValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Required"),
  password: Yup.string().required("Required"),
});

const LoginForm = ({ loginError, inProgress }: any) => {
  const { login } = useLogin();
  const { search } = window.location;
  const query =
    search &&
    new URLSearchParams(search.replace("+", "_plusSign_"))
      ?.get?.("email")
      ?.replace("_plusSign_", "+");

  return (
    <Formik
      enableReinitialize
      initialValues={{
        password: "",
        email: query || "",
      }}
      validationSchema={loginValidationSchema}
      validateOnBlur={false}
      onSubmit={async (values) => await login(values)}
    >
      <Form>
        <AuthFormContainer>
          <Text textStyle="h3" mb="12" textAlign="center">
            Welcome back to Tiger&nbsp;Cloud
          </Text>
          <Field name="email">
            {({ field, meta }: FieldProps) => (
              <InputGroup
                error={meta.touched && meta.error ? meta.error : null}
                placeholder="Your email"
                label="Email address"
                layoutProps={{ mb: "8" }}
                data-cy="email"
                {...field}
              />
            )}
          </Field>
          <Field name="password">
            {({ field, meta }: FieldProps) => (
              <PasswordInputGroup
                autocomplete="on"
                passwordType="enter"
                error={
                  (loginError && capitalize(loginError)) ||
                  (meta.touched && meta.error ? meta.error : null)
                }
                placeholder="Your password"
                label="Password"
                data-cy="password"
                layoutProps={{ mb: "3" }}
                {...field}
              />
            )}
          </Field>
          <ForgotPasswordLink mb="10" />
          <Button
            type="submit"
            data-cy="account-login"
            size="lg"
            maxW="unset"
            w="100%"
            mb="2"
            isLoading={inProgress}
          >
            Log in
          </Button>

          <GoogleSSOButton text="continue_with" />

          <Flex gap="1" mt="8" justifyContent="center">
            <Link data-cy="create-account" as={RouterLink} to="/signup">
              Create an account
            </Link>
          </Flex>
        </AuthFormContainer>
      </Form>
    </Formik>
  );
};

const LoginComponent = observer(function LoginComponent() {
  const { authStore } = useStores();
  const { login } = useLogin();
  const { forgotPassword, errors, resendEmail, inProgress, twoFactorId } =
    authStore;

  useEffect(() => {
    Userpilot.reload();
    return () => authStore.resetEmailFlow();
  }, [authStore]);

  if (resendEmail) {
    return <ResendEmail />;
  }

  if (forgotPassword) {
    return <ForgotPassword />;
  }
  if (twoFactorId) {
    return (
      <AuthFormContainer>
        <Text textStyle="h3" mb="10" textAlign="center">
          Two-factor authentication
        </Text>
        <MfaChallenge
          canUseRecoveryCode={true}
          verify={login}
          size="lg"
          confirmText="Continue"
          onCancel={() => {
            authStore.clearTwoFactorId();
          }}
          appText="Please enter the code from your authenticator app to log in."
          recoverySubText="Enter recovery code to confirm."
        />
      </AuthFormContainer>
    );
  }

  const loginError = errors && "message" in errors ? errors.message : undefined;

  return <LoginForm loginError={loginError} inProgress={inProgress} />;
});

export const LoginPage = observer(function LoginPage() {
  const routerState = useRouterState();
  const { accountDeleted, email } = routeApi.useSearch();
  const {
    authStore: { errors },
  } = useStores();

  if (routerState.isLoading && !errors) {
    return <Loader size="lg" />;
  }

  if (accountDeleted) {
    return (
      <Navigate to="/account-deletion-result" search={{ email }} replace />
    );
  }

  return (
    <Flex
      w="100%"
      gap="36"
      flexWrap="wrap"
      justifyContent="center"
      alignItems="center"
    >
      <LandingCopyComponent
        header="Postgres for time-series workloads at any scale."
        tagline="Real-world scale on a single Tiger Cloud service."
        body="Deploy on AWS or Microsoft Azure."
      />

      {/*
        The card's width comes from the layout rather than its contents: the
        Google SSO button only appears once Google's script has painted it, and
        a content-sized card would grow and re-center the page at that moment.
      */}
      <Box w={AUTH_CARD_WIDTH} maxW="100%">
        <LoginComponent />
      </Box>
    </Flex>
  );
});
